import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay, addDays, isBefore, isAfter } from 'date-fns';
import { it } from 'date-fns/locale';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import { useAuth } from '../../contexts/AuthContext';
import { promotionService } from '../../services/promotionService';
import { vetrineService } from '../../services/vetrineService';

const locales = {
  'it': it,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

const AdminPromotionsCalendar = () => {
  const navigate = useNavigate();
  const { user, userRole } = useAuth();
  const [events, setEvents] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [view, setView] = useState('month');
  const [date, setDate] = useState(new Date());
  const [selectedPromotion, setSelectedPromotion] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterStore, setFilterStore] = useState('all');
  const [filterZone, setFilterZone] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');

  const statusOptions = [
    { value: 'all', label: 'Tutti gli stati' },
    { value: 'draft', label: 'Bozza' },
    { value: 'active', label: 'Attiva' },
    { value: 'scheduled', label: 'Futura' },
    { value: 'expired', label: 'Scaduta' },
    { value: 'archived', label: 'Archiviata' }
  ];

  const viewOptions = [
    { value: 'month', label: 'Mese' },
    { value: 'week', label: 'Settimana' },
    { value: 'day', label: 'Giorno' },
    { value: 'agenda', label: 'Agenda' }
  ];

  // Get unique zones from stores
  const zoneOptions = [
    { value: 'all', label: 'Tutte le zone' },
    ...Array.from(new Set(stores?.map(s => s?.zona)?.filter(Boolean)))?.map(zone => ({
      value: zone,
      label: zone
    }))
  ];

  // Get unique categories from stores
  const categoryOptions = [
    { value: 'all', label: 'Tutte le categorie' },
    ...Array.from(new Set(stores?.map(s => s?.categoria)?.filter(Boolean)))?.map(cat => ({
      value: cat,
      label: cat
    }))
  ];

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    if (userRole !== 'admin') {
      navigate('/');
      return;
    }

    loadData();
  }, [user, userRole, navigate]);

  useEffect(() => {
    convertPromotionsToEvents();
  }, [promotions, filterStatus, filterStore, filterZone, filterCategory]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [promotionsData, storesData] = await Promise.all([
        promotionService?.getAllPromotions(),
        vetrineService?.getAllStores()
      ]);
      setPromotions(promotionsData);
      setStores(storesData);
    } catch (err) {
      setError(err?.message || 'Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const getPromotionStatus = (promo) => {
    const now = new Date();
    const start = new Date(promo?.startDate);
    const end = new Date(promo?.endDate);

    if (promo?.status === 'archived') return 'archived';
    if (promo?.status === 'draft') return 'draft';
    if (isAfter(now, end)) return 'expired';
    if (isBefore(now, start)) return 'scheduled';
    return 'active';
  };

  const convertPromotionsToEvents = () => {
    let filtered = promotions;

    if (filterStatus !== 'all') {
      filtered = filtered?.filter(p => {
        const actualStatus = getPromotionStatus(p);
        return actualStatus === filterStatus;
      });
    }

    if (filterStore !== 'all') {
      filtered = filtered?.filter(p => p?.storeId === filterStore);
    }

    if (filterZone !== 'all') {
      filtered = filtered?.filter(p => p?.vetrine_stores?.zona === filterZone);
    }

    if (filterCategory !== 'all') {
      filtered = filtered?.filter(p => p?.vetrine_stores?.categoria === filterCategory);
    }

    const calendarEvents = filtered?.map(promo => {
      const actualStatus = getPromotionStatus(promo);
      const storeName = promo?.vetrine_stores?.nome_negozio || 'N/A';
      
      return {
        id: promo?.id,
        title: `${storeName} - ${promo?.titolo}`,
        start: new Date(promo?.startDate),
        end: new Date(promo?.endDate),
        resource: {
          ...promo,
          actualStatus,
          storeName
        },
        allDay: false
      };
    });

    setEvents(calendarEvents);
  };

  const handleEventDrop = async ({ event, start, end }) => {
    try {
      const promotion = event?.resource;
      const duration = new Date(promotion?.endDate) - new Date(promotion?.startDate);
      const newEndDate = new Date(start?.getTime() + duration);

      await promotionService?.updatePromotion(promotion?.id, {
        startDate: start?.toISOString(),
        endDate: newEndDate?.toISOString()
      });

      setSuccess('Promozione riprogrammata con successo');
      await loadData();
    } catch (err) {
      setError(err?.message || 'Errore nella riprogrammazione');
    }
  };

  const handleEventResize = async ({ event, start, end }) => {
    try {
      const promotion = event?.resource;

      await promotionService?.updatePromotion(promotion?.id, {
        startDate: start?.toISOString(),
        endDate: end?.toISOString()
      });

      setSuccess('Durata promozione aggiornata con successo');
      await loadData();
    } catch (err) {
      setError(err?.message || 'Errore nell\'aggiornamento della durata');
    }
  };

  const handleSelectEvent = (event) => {
    setSelectedPromotion(event?.resource);
  };

  const handleSelectSlot = ({ start, end }) => {
    navigate('/admin-promotions', { state: { startDate: start, endDate: end } });
  };

  const eventStyleGetter = (event) => {
    const promotion = event?.resource;
    const actualStatus = promotion?.actualStatus || 'draft';
    let backgroundColor = '#6B7280';
    let borderColor = '#4B5563';

    switch (actualStatus) {
      case 'active':
        backgroundColor = '#10B981';
        borderColor = '#059669';
        break;
      case 'scheduled':
        backgroundColor = '#F59E0B';
        borderColor = '#D97706';
        break;
      case 'expired':
        backgroundColor = '#6B7280';
        borderColor = '#4B5563';
        break;
      case 'draft':
        backgroundColor = '#8B7355';
        borderColor = '#6B5744';
        break;
      case 'archived':
        backgroundColor = '#EF4444';
        borderColor = '#DC2626';
        break;
    }

    return {
      style: {
        backgroundColor,
        borderColor,
        borderWidth: '2px',
        borderStyle: 'solid',
        borderRadius: '6px',
        color: '#FFFFFF',
        fontSize: '13px',
        padding: '4px 8px',
        fontWeight: '500'
      }
    };
  };

  const handleBulkAction = async (action) => {
    const visiblePromotionIds = events?.map(e => e?.id);
    
    if (visiblePromotionIds?.length === 0) {
      setError('Nessuna promozione visibile per l\'azione selezionata');
      return;
    }

    if (!window.confirm(`Sei sicuro di voler ${action === 'archive' ? 'archiviare' : 'eliminare'} ${visiblePromotionIds?.length} promozioni?`)) {
      return;
    }

    try {
      if (action === 'archive') {
        await Promise.all(
          visiblePromotionIds?.map(id => 
            promotionService?.updatePromotion(id, { status: 'archived' })
          )
        );
        setSuccess(`${visiblePromotionIds?.length} promozioni archiviate con successo`);
      } else if (action === 'delete') {
        await Promise.all(
          visiblePromotionIds?.map(id => promotionService?.deletePromotion(id))
        );
        setSuccess(`${visiblePromotionIds?.length} promozioni eliminate con successo`);
      }
      await loadData();
    } catch (err) {
      setError(err?.message || 'Errore nell\'azione di massa');
    }
  };

  const storeOptions = [
    { value: 'all', label: 'Tutti i negozi' },
    ...stores?.map(store => ({
      value: store?.id,
      label: store?.nomeNegozio
    }))
  ];

  const handleViewChange = (newView) => {
    setView(newView);
  };

  const handleNavigate = (newDate) => {
    setDate(newDate);
  };

  const CustomToolbar = (toolbar) => {
    const goToBack = () => {
      toolbar?.onNavigate('PREV');
    };

    const goToNext = () => {
      toolbar?.onNavigate('NEXT');
    };

    const goToToday = () => {
      toolbar?.onNavigate('TODAY');
    };

    const label = () => {
      const date = toolbar?.date;
      if (view === 'month') {
        return format(date, 'MMMM yyyy', { locale: it });
      } else if (view === 'week') {
        const start = startOfWeek(date, { locale: it });
        const end = addDays(start, 6);
        return `${format(start, 'd MMM', { locale: it })} - ${format(end, 'd MMM yyyy', { locale: it })}`;
      } else if (view === 'day') {
        return format(date, 'dd MMMM yyyy', { locale: it });
      }
      return format(date, 'MMMM yyyy', { locale: it });
    };

    return (
      <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <Button onClick={goToToday} className="px-4 py-2 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50">
            Oggi
          </Button>
          <div className="flex items-center gap-1">
            <Button onClick={goToBack} className="p-2 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50">
              <Icon name="chevron-left" className="w-5 h-5" />
            </Button>
            <Button onClick={goToNext} className="p-2 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50">
              <Icon name="chevron-right" className="w-5 h-5" />
            </Button>
          </div>
        </div>
        <h2 className="text-xl font-semibold text-gray-900 capitalize">{label()}</h2>
        <div className="w-48">
          <Select
            value={view}
            onChange={(e) => handleViewChange(e?.target?.value)}
            options={viewOptions}
          />
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header onFilterClick={() => {}} />
        <div className="pt-24 pb-20 px-6">
          <div className="max-w-7xl mx-auto flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Calendario Promozioni Admin - CLICCAMI.NET</title>
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header onFilterClick={() => {}} />
        <div className="pt-24 pb-20 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="font-heading font-bold text-4xl text-foreground mb-2">
                  Calendario Promozioni
                </h1>
                <p className="text-muted-foreground">Pianifica, programma e gestisci le promozioni con drag-and-drop</p>
              </div>
              <div className="flex items-center gap-3">
                <Button
                  onClick={() => navigate('/admin-promotions')}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <Icon name="List" size={20} />
                  Vista Lista
                </Button>
                <Button
                  onClick={() => handleSelectSlot({ start: new Date(), end: addDays(new Date(), 30) })}
                  className="flex items-center gap-2"
                >
                  <Icon name="Plus" size={20} />
                  Nuova Promozione
                </Button>
              </div>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-destructive/10 border border-destructive rounded-lg text-destructive flex items-center justify-between">
                <span>{error}</span>
                <Button variant="ghost" size="icon" onClick={() => setError('')}>
                  <Icon name="X" size={16} />
                </Button>
              </div>
            )}

            {success && (
              <div className="mb-6 p-4 bg-success/10 border border-success rounded-lg text-success flex items-center justify-between">
                <span>{success}</span>
                <Button variant="ghost" size="icon" onClick={() => setSuccess('')}>
                  <Icon name="X" size={16} />
                </Button>
              </div>
            )}

            <div className="bg-white rounded-lg shadow-sm border border-border p-6 mb-6">
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <div className="flex-1 min-w-[200px]">
                  <Select
                    value={filterStatus}
                    onChange={setFilterStatus}
                    options={statusOptions}
                    placeholder="Filtra per stato"
                  />
                </div>
                <div className="flex-1 min-w-[200px]">
                  <Select
                    value={filterStore}
                    onChange={setFilterStore}
                    options={storeOptions}
                    placeholder="Filtra per negozio"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => handleBulkAction('archive')}
                    variant="outline"
                    disabled={events?.length === 0}
                    className="flex items-center gap-2"
                  >
                    <Icon name="Archive" size={16} />
                    Archivia Visibili
                  </Button>
                  <Button
                    onClick={() => handleBulkAction('delete')}
                    variant="destructive"
                    disabled={events?.length === 0}
                    className="flex items-center gap-2"
                  >
                    <Icon name="Trash2" size={16} />
                    Elimina Visibili
                  </Button>
                </div>
              </div>

              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded" style={{ backgroundColor: '#10B981' }}></div>
                  <span>Attiva</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded" style={{ backgroundColor: '#F59E0B' }}></div>
                  <span>Futura</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded" style={{ backgroundColor: '#6B7280' }}></div>
                  <span>Scaduta</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded" style={{ backgroundColor: '#EF4444' }}></div>
                  <span>Archiviata</span>
                </div>
              </div>
            </div>

            <DndProvider backend={HTML5Backend}>
              <div className="bg-white rounded-lg shadow-sm border border-border p-6" style={{ height: '700px' }}>
                <Calendar
                  localizer={localizer}
                  events={events}
                  startAccessor="start"
                  endAccessor="end"
                  style={{ height: '100%' }}
                  view={view}
                  onView={setView}
                  date={date}
                  onNavigate={setDate}
                  onSelectEvent={handleSelectEvent}
                  onSelectSlot={handleSelectSlot}
                  onEventDrop={handleEventDrop}
                  onEventResize={handleEventResize}
                  eventPropGetter={eventStyleGetter}
                  selectable
                  resizable
                  draggableAccessor={() => true}
                  culture="it"
                  messages={{
                    next: 'Successivo',
                    previous: 'Precedente',
                    today: 'Oggi',
                    month: 'Mese',
                    week: 'Settimana',
                    day: 'Giorno',
                    agenda: 'Agenda',
                    date: 'Data',
                    time: 'Ora',
                    event: 'Evento',
                    noEventsInRange: 'Nessuna promozione in questo periodo',
                    showMore: (total) => `+ ${total} altre`
                  }}
                  components={{
                    Toolbar: CustomToolbar
                  }}
                />
              </div>
            </DndProvider>

            {selectedPromotion && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedPromotion(null)}>
                <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6" onClick={(e) => e?.stopPropagation()}>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="font-heading font-bold text-2xl text-foreground">{selectedPromotion?.titolo}</h2>
                    <Button variant="ghost" size="icon" onClick={() => setSelectedPromotion(null)}>
                      <Icon name="X" size={20} />
                    </Button>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">Descrizione</p>
                      <p className="text-foreground">{selectedPromotion?.descrizione}</p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm font-medium text-muted-foreground mb-1">Data Inizio</p>
                        <p className="text-foreground">{format(new Date(selectedPromotion?.startDate), 'dd MMMM yyyy', { locale: it })}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-muted-foreground mb-1">Data Fine</p>
                        <p className="text-foreground">{format(new Date(selectedPromotion?.endDate), 'dd MMMM yyyy', { locale: it })}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm font-medium text-muted-foreground mb-1">Tipo Sconto</p>
                        <p className="text-foreground capitalize">{selectedPromotion?.discountType?.replace('_', ' ')}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-muted-foreground mb-1">Valore Sconto</p>
                        <p className="text-foreground">{selectedPromotion?.discountValue || 'N/A'}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-4 gap-4">
                      <div className="bg-muted rounded-lg p-3">
                        <p className="text-xs text-muted-foreground mb-1">Visualizzazioni</p>
                        <p className="text-lg font-bold text-foreground">{selectedPromotion?.viewCount || 0}</p>
                      </div>
                      <div className="bg-muted rounded-lg p-3">
                        <p className="text-xs text-muted-foreground mb-1">Click</p>
                        <p className="text-lg font-bold text-foreground">{selectedPromotion?.clickCount || 0}</p>
                      </div>
                      <div className="bg-muted rounded-lg p-3">
                        <p className="text-xs text-muted-foreground mb-1">Condivisioni</p>
                        <p className="text-lg font-bold text-foreground">{selectedPromotion?.shareCount || 0}</p>
                      </div>
                      <div className="bg-muted rounded-lg p-3">
                        <p className="text-xs text-muted-foreground mb-1">Conversioni</p>
                        <p className="text-lg font-bold text-foreground">{selectedPromotion?.conversionCount || 0}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 pt-4">
                      <Button
                        onClick={() => {
                          navigate('/admin-promotions');
                          setSelectedPromotion(null);
                        }}
                        className="flex-1"
                      >
                        Modifica Promozione
                      </Button>
                      <Button
                        onClick={() => setSelectedPromotion(null)}
                        variant="outline"
                        className="flex-1"
                      >
                        Chiudi
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminPromotionsCalendar;